import { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SubmissionDraftContext } from "../context/SubmissionDraftContext";
import CustomDropdown from "../components/CustomDropdown";
import { COUNTRY_OPTIONS } from "../data/submissionData";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { isEmail } from "../utils/validators";

const createAuthor = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  name: ""
});

export default function ArticleDetails() {
  const navigate = useNavigate();
  const { draft, updateDraft } = useContext(SubmissionDraftContext);
  const [error, setError] = useState("");

  const authors = useMemo(
    () => (Array.isArray(draft.authors) && draft.authors.length ? draft.authors : [createAuthor()]),
    [draft.authors]
  );

  const phoneOptions = useMemo(
    () =>
      COUNTRY_OPTIONS.map((item) => ({
        value: item.dialCode,
        label: `${item.name} (${item.dialCode})`
      })),
    []
  );

  const countryOptions = useMemo(
    () => COUNTRY_OPTIONS.map((item) => ({ value: item.name, label: item.name })),
    []
  );

  const setAuthors = (nextAuthors) => {
    updateDraft({ authors: nextAuthors });
  };

  const updateAuthor = (authorId, key, value) => {
    setAuthors(
      authors.map((author) =>
        author.id === authorId ? { ...author, [key]: value } : author
      )
    );
  };

  // const addAuthor = () => {
  //   setAuthors([...authors, createAuthor()]);
  // };

  // const removeAuthor = (authorId) => {
  //   const nextAuthors = authors.filter((author) => author.id !== authorId);
  //   const safeAuthors = nextAuthors.length ? nextAuthors : [createAuthor()];
  //   updateDraft({ authors: safeAuthors });
  // };

  const clearPage = () => {
    updateDraft({
      authors: [createAuthor()],
      correspondenceName: "",
      correspondenceEmail: "",
      countryCode: "+91",
      phone: "",
      address: "",
      country: "India"
    });
  };

  const onNext = () => {
    if (!draft.correspondenceName?.trim() || !draft.correspondenceEmail?.trim()) {
      setError("Correspondence author and email are required.");
      return;
    }

    if (!isEmail(draft.correspondenceEmail)) {
      setError("Correspondence email must be valid.");
      return;
    }

    if (!authors.some((author) => author.name.trim())) {
      setError("Add at least one author.");
      return;
    }

    const phoneValue = draft.phone.trim();
    if (
      !draft.countryCode.trim() ||
      !phoneValue ||
      !draft.address.trim() ||
      !draft.country.trim()
    ) {
      setError("Country code, phone, address and country are required.");
      return;
    }

    if (!/^\d{7,15}$/.test(phoneValue)) {
      setError("Phone number must contain only digits (7 to 15).");
      return;
    }

    const selectedCountry =
      COUNTRY_OPTIONS.find((item) => item.name === draft.country) ||
      COUNTRY_OPTIONS.find((item) => item.dialCode === draft.countryCode);
    if (!selectedCountry) {
      setError("Please select a valid country.");
      return;
    }

    const parsedPhone = parsePhoneNumberFromString(phoneValue, selectedCountry.code);
    if (!parsedPhone || !parsedPhone.isValid()) {
      setError(`Enter a valid phone number for ${selectedCountry.name}.`);
      return;
    }

    if (draft.address.trim().length < 10) {
      setError("Work address must be at least 10 characters.");
      return;
    }

    setError("");
    navigate("/journal-details");
  };

  return (
    <div className="workspace-card section-card">
      <div className="section-header-row">
        <h2>Author Details</h2>
        <button className="toolbar-btn compact-btn" onClick={clearPage}>
          Clear
        </button>
      </div>

      <div className="inline-authors-wrap">
        {authors.map((author, index) => (
          <div key={author.id} className="inline-author-row">
            <label className="toolbar-label">Author name</label>
            <input
              className="toolbar-input"
              placeholder="Author name"
              value={author.name}
              onChange={(event) => updateAuthor(author.id, "name", event.target.value)}
            />
          </div>
        ))}
      </div>

      <h3 className="section-subtitle">Correspondence</h3>
      <div className="details-form-grid">
        <label>Correspondence author</label>
        <div>
          <input
            className="toolbar-input"
            placeholder="Correspondence author"
            value={draft.correspondenceName}
            onChange={(event) => updateDraft({ correspondenceName: event.target.value })}
          />
        </div>

        <label>Correspondence Email</label>
        <div>
          <input
            className="toolbar-input"
            placeholder="Correspondence email"
            value={draft.correspondenceEmail}
            onChange={(event) => updateDraft({ correspondenceEmail: event.target.value })}
          />
        </div>
      </div>

      <h3 className="section-subtitle">Contact</h3>
      <div className="details-form-grid">
        <label>Phone Number</label>
        <div>
          <div className="field-row">
            <CustomDropdown
              className="toolbar-select"
              searchable
              options={phoneOptions}
              value={draft.countryCode}
              onChange={(nextValue) => updateDraft({ countryCode: nextValue })}
            />
            <input
              className="toolbar-input"
              value={draft.phone}
              onChange={(event) =>
                updateDraft({ phone: event.target.value.replace(/\D/g, "").slice(0, 15) })
              }
              placeholder="Phone number"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={15}
            />
          </div>
        </div>

        <label>Work Address</label>
        <div>
          <textarea
            className="toolbar-input address-area"
            value={draft.address}
            onChange={(event) => updateDraft({ address: event.target.value })}
            rows={4}
          />
        </div>

        <label>Country</label>
        <div>
          <CustomDropdown
            className="toolbar-select"
            searchable
            options={countryOptions}
            value={draft.country}
            onChange={(nextValue) => {
              const selected = COUNTRY_OPTIONS.find((item) => item.name === nextValue);
              updateDraft({
                country: nextValue,
                countryCode: selected?.dialCode || draft.countryCode
              });
            }}
          />
        </div>
      </div>

      {error && <p className="submit-error">{error}</p>}

      <div className="wizard-actions">
        <button className="toolbar-btn primary-btn" onClick={onNext}>
          Next
        </button>
      </div>
    </div>
  );
}
